import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Container, Header, Segment, Loader, Message, Accordion, Icon, List, Button } from 'semantic-ui-react';
import useVoiceLabCompositeData from '../../hooks/useVoiceLabCompositeData';
import {
  selectFlatPortfolios,
  selectFlatCharacters,
  selectFlatVariations,
  selectPortfoliosByActor,
  selectCharactersByPortfolioId,
  selectVariationsByCharacterId
} from '../../slices/voiceActorSlice';

const VoiceLabNestedTest = () => {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selectedActorId, setSelectedActorId] = useState('all');
  const [selectedPortfolioId, setSelectedPortfolioId] = useState('all');
  const [selectedCharacterId, setSelectedCharacterId] = useState('all');

  // Fetch data using the composite hook
  const {
    actorUsers,
    isLoading,
    error,
    flatPortfolios,
    flatCharacters,
    flatVariations
  } = useVoiceLabCompositeData();

  // Use selectors to get data from Redux
  const portfolios = useSelector(state => selectFlatPortfolios(state));
  const characters = useSelector(state => selectFlatCharacters(state));
  const variations = useSelector(state => selectFlatVariations(state));

  // Use the nested structure selectors
  const portfoliosByActor = useSelector(state =>
    selectPortfoliosByActor(state, selectedActorId)
  );

  const charactersByPortfolio = useSelector(state =>
    selectCharactersByPortfolioId(state, selectedPortfolioId)
  );

  const variationsByCharacter = useSelector(state =>
    selectVariationsByCharacterId(state, selectedCharacterId)
  );

  // Handle accordion click
  const handleAccordionClick = (e, titleProps) => {
    const { index } = titleProps;
    const newIndex = activeIndex === index ? -1 : index;
    setActiveIndex(newIndex);
  };

  // Reset filters
  const handleResetFilters = () => {
    setSelectedActorId('all');
    setSelectedPortfolioId('all');
    setSelectedCharacterId('all');
  };

  if (isLoading) {
    return (
      <Container style={{ marginTop: '2em' }}>
        <Loader active>Loading Voice Lab Data...</Loader>
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ marginTop: '2em' }}>
        <Message negative>
          <Message.Header>Error Loading Data</Message.Header>
          <p>{error}</p>
        </Message>
      </Container>
    );
  }

  return (
    <Container style={{ marginTop: '2em' }}>
      <Header as="h1">Voice Lab Nested Structure Test</Header>

      <Segment>
        <Header as="h3">Filters</Header>
        <div style={{ marginBottom: '1em' }}>
          <Button.Group>
            <Button
              primary={selectedActorId === 'all'}
              onClick={() => setSelectedActorId('all')}
            >
              All Actors
            </Button>
            {actorUsers.map(actor => (
              <Button
                key={actor.uuid}
                primary={selectedActorId === actor.uuid}
                onClick={() => setSelectedActorId(actor.uuid)}
              >
                {actor.displayName || actor.name || actor.email || actor.uuid.substring(0, 8)}
              </Button>
            ))}
          </Button.Group>
        </div>

        <div style={{ marginBottom: '1em' }}>
          <Button.Group>
            <Button
              primary={selectedPortfolioId === 'all'}
              onClick={() => setSelectedPortfolioId('all')}
            >
              All Portfolios
            </Button>
            {portfoliosByActor.slice(0, 5).map(portfolio => (
              <Button
                key={portfolio.uuid}
                primary={selectedPortfolioId === portfolio.uuid}
                onClick={() => setSelectedPortfolioId(portfolio.uuid)}
              >
                {portfolio.name || portfolio.uuid.substring(0, 8)}
              </Button>
            ))}
          </Button.Group>
        </div>

        <div style={{ marginBottom: '1em' }}>
          <Button.Group>
            <Button
              primary={selectedCharacterId === 'all'}
              onClick={() => setSelectedCharacterId('all')}
            >
              All Characters
            </Button>
            {charactersByPortfolio.slice(0, 5).map(character => (
              <Button
                key={character.uuid}
                primary={selectedCharacterId === character.uuid}
                onClick={() => setSelectedCharacterId(character.uuid)}
              >
                {character.name || character.uuid.substring(0, 8)}
              </Button>
            ))}
          </Button.Group>
        </div>

        <Button onClick={handleResetFilters}>Reset Filters</Button>
      </Segment>

      <Accordion fluid styled>
        <Accordion.Title
          active={activeIndex === 0}
          index={0}
          onClick={handleAccordionClick}
        >
          <Icon name="dropdown" />
          Actors ({actorUsers.length})
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 0}>
          <List divided relaxed>
            {actorUsers.map(actor => (
              <List.Item key={actor.uuid}>
                <List.Icon name="user" size="large" verticalAlign="middle" />
                <List.Content>
                  <List.Header>{actor.displayName || actor.name || actor.email || actor.uuid}</List.Header>
                  <List.Description>
                    Portfolios: {actor.portfolios?.length || 0}
                  </List.Description>
                </List.Content>
              </List.Item>
            ))}
          </List>
        </Accordion.Content>

        <Accordion.Title
          active={activeIndex === 1}
          index={1}
          onClick={handleAccordionClick}
        >
          <Icon name="dropdown" />
          Flat Portfolios ({portfolios.length})
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 1}>
          <List divided relaxed>
            {portfolios.slice(0, 10).map(portfolio => (
              <List.Item key={portfolio.uuid}>
                <List.Icon name="folder" size="large" verticalAlign="middle" />
                <List.Content>
                  <List.Header>{portfolio.name}</List.Header>
                  <List.Description>
                    Actor ID: {portfolio.actorId}, Characters: {portfolio.characters?.length || 0}
                  </List.Description>
                </List.Content>
              </List.Item>
            ))}
            {portfolios.length > 10 && (
              <List.Item>
                <List.Content>
                  <List.Description>
                    ...and {portfolios.length - 10} more
                  </List.Description>
                </List.Content>
              </List.Item>
            )}
          </List>
        </Accordion.Content>

        <Accordion.Title
          active={activeIndex === 2}
          index={2}
          onClick={handleAccordionClick}
        >
          <Icon name="dropdown" />
          Flat Characters ({characters.length})
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 2}>
          <List divided relaxed>
            {characters.slice(0, 10).map(character => (
              <List.Item key={character.uuid}>
                <List.Icon name="user outline" size="large" verticalAlign="middle" />
                <List.Content>
                  <List.Header>{character.name}</List.Header>
                  <List.Description>
                    Portfolio ID: {character.portfolioId}, Actor ID: {character.actorId}
                  </List.Description>
                </List.Content>
              </List.Item>
            ))}
            {characters.length > 10 && (
              <List.Item>
                <List.Content>
                  <List.Description>
                    ...and {characters.length - 10} more
                  </List.Description>
                </List.Content>
              </List.Item>
            )}
          </List>
        </Accordion.Content>

        <Accordion.Title
          active={activeIndex === 3}
          index={3}
          onClick={handleAccordionClick}
        >
          <Icon name="dropdown" />
          Flat Variations ({variations.length})
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 3}>
          <List divided relaxed>
            {variations.slice(0, 10).map(variation => (
              <List.Item key={variation.uuid}>
                <List.Icon name="sound" size="large" verticalAlign="middle" />
                <List.Content>
                  <List.Header>{variation.name}</List.Header>
                  <List.Description>
                    Character ID: {variation.characterId},
                    Portfolio ID: {variation.portfolioId},
                    Actor ID: {variation.actorId}
                  </List.Description>
                </List.Content>
              </List.Item>
            ))}
            {variations.length > 10 && (
              <List.Item>
                <List.Content>
                  <List.Description>
                    ...and {variations.length - 10} more
                  </List.Description>
                </List.Content>
              </List.Item>
            )}
          </List>
        </Accordion.Content>

        <Accordion.Title
          active={activeIndex === 4}
          index={4}
          onClick={handleAccordionClick}
        >
          <Icon name="dropdown" />
          Filtered Portfolios ({portfoliosByActor.length})
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 4}>
          <List divided relaxed>
            {portfoliosByActor.slice(0, 10).map(portfolio => (
              <List.Item key={portfolio.uuid}>
                <List.Icon name="folder" size="large" verticalAlign="middle" />
                <List.Content>
                  <List.Header>{portfolio.name}</List.Header>
                  <List.Description>
                    Actor ID: {portfolio.actorId}, Characters: {portfolio.characters?.length || 0}
                  </List.Description>
                </List.Content>
              </List.Item>
            ))}
            {portfoliosByActor.length > 10 && (
              <List.Item>
                <List.Content>
                  <List.Description>
                    ...and {portfoliosByActor.length - 10} more
                  </List.Description>
                </List.Content>
              </List.Item>
            )}
          </List>
        </Accordion.Content>

        <Accordion.Title
          active={activeIndex === 5}
          index={5}
          onClick={handleAccordionClick}
        >
          <Icon name="dropdown" />
          Filtered Characters ({charactersByPortfolio.length})
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 5}>
          <List divided relaxed>
            {charactersByPortfolio.slice(0, 10).map(character => (
              <List.Item key={character.uuid}>
                <List.Icon name="user outline" size="large" verticalAlign="middle" />
                <List.Content>
                  <List.Header>{character.name}</List.Header>
                  <List.Description>
                    Portfolio ID: {character.portfolioId}, Actor ID: {character.actorId}
                  </List.Description>
                </List.Content>
              </List.Item>
            ))}
            {charactersByPortfolio.length > 10 && (
              <List.Item>
                <List.Content>
                  <List.Description>
                    ...and {charactersByPortfolio.length - 10} more
                  </List.Description>
                </List.Content>
              </List.Item>
            )}
          </List>
        </Accordion.Content>

        <Accordion.Title
          active={activeIndex === 6}
          index={6}
          onClick={handleAccordionClick}
        >
          <Icon name="dropdown" />
          Filtered Variations ({variationsByCharacter.length})
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 6}>
          <List divided relaxed>
            {variationsByCharacter.slice(0, 10).map(variation => (
              <List.Item key={variation.uuid}>
                <List.Icon name="sound" size="large" verticalAlign="middle" />
                <List.Content>
                  <List.Header>{variation.name}</List.Header>
                  <List.Description>
                    Character ID: {variation.characterId},
                    Portfolio ID: {variation.portfolioId},
                    Actor ID: {variation.actorId}
                  </List.Description>
                </List.Content>
              </List.Item>
            ))}
            {variationsByCharacter.length > 10 && (
              <List.Item>
                <List.Content>
                  <List.Description>
                    ...and {variationsByCharacter.length - 10} more
                  </List.Description>
                </List.Content>
              </List.Item>
            )}
          </List>
        </Accordion.Content>
      </Accordion>
    </Container>
  );
};

export default VoiceLabNestedTest;
